import { useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Link, useParams } from "react-router-dom"
import { StatusBadge } from "./TicketsPage"
import { BerichtsThread } from "@/components/BerichtsThread"
import { EmployeePicker, useEmployeeLabel } from "@/components/EmployeePicker"
import { Badge, Button, Input, Select, Textarea } from "@/components/ui"
import { useToast, fehlerNachricht } from "@/lib/toast"
import {
  TICKET_ART_LABEL,
  entferneTicketMitarbeiter,
  fetchTicket,
  fetchZeitbuchungen,
  loescheTicketLieferanten,
  recalcTicketStartDate,
  updateTicket,
  updateTicketStoeranalyse,
  zuordneTicketLieferant,
  zuordneTicketMitarbeiter,
} from "@/lib/api"

const TABS = ["herkunft", "berichte", "mitarbeiter", "zeiten", "fremdfirmen"] as const
type Tab = (typeof TABS)[number]

// 09-Regel A.34 „Link zur Aufgabe" (Deep-Link, AP-4.6): Produktions-Vhost
// steht laut OFFENE_PUNKTE #14 noch aus — die Route `/tickets/:id` existiert
// bereits, daher relativer Pfad + window.location.origin zur Laufzeit statt
// einer fest verdrahteten Domain (funktioniert automatisch für
// technik[-stage].rmnc.app, sobald der Vhost angelegt ist).
function ticketDeepLink(ticketId: number): string {
  return `${window.location.origin}/tickets/${ticketId}`
}

// 09-Regel H.1 „Handbuch anzeigen" (Deep-Link, AP-4.6) — Muster identisch zu
// DashboardPage.tsx (HANDBUCH_URL, 02-Regel 38), hier auf das
// Servicetickets-Kapitel des BookStack-Buchs verweisend.
const HANDBUCH_URL_TICKETS = "https://dokubase.rmnc.app/books/technik-instandhaltung/page/servicetickets"

const TAB_LABEL: Record<Tab, string> = {
  herkunft: "Herkunft",
  berichte: "Berichte",
  mitarbeiter: "Mitarbeiter",
  zeiten: "Zeiten",
  fremdfirmen: "Fremdfirmen",
}

/** Ticket-Detail (AP-4.3): Herkunft, Mitarbeiterzuordnung + Abschlusstracking
 * (Auto-Close A.2b/A.33/A.39 läuft serverseitig), Zeiten-Anzeige (Schema-Join
 * auf TimeTab-Zeiten), Fremdfirmen-Beteiligung (09-D).
 *
 * Route-Wrapper (Deep-Link-Fallback, `/tickets/:id`) — liest die ID aus der
 * URL und delegiert an `TicketDetailContent`. Der primäre Aufrufweg ist das
 * Sheet aus `TicketsPage` (UX_KONZEPT.md §7), das dieselbe Content-Komponente
 * einbettet. */
export function TicketDetailPage() {
  const params = useParams<{ id: string }>()
  const ticketId = Number(params.id)
  return (
    <div>
      <Link
        to="/tickets"
        className="mb-2 inline-block rounded-md text-sm text-slate-500 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
      >
        ← zurück zur Liste
      </Link>
      <TicketDetailContent ticketId={ticketId} />
    </div>
  )
}

/** Inhalt der Ticket-Detailansicht — wird sowohl von der Route (`TicketDetailPage`,
 * Deep-Link-Fallback) als auch vom Sheet (`TicketDetailSheet` in `TicketsPage`)
 * verwendet, damit Zeilen-Klick in der Liste ein Sheet öffnet statt zu navigieren
 * (UX_KONZEPT.md §7). */
export function TicketDetailContent({ ticketId }: { ticketId: number }) {
  const queryClient = useQueryClient()
  const toast = useToast()
  const [tab, setTab] = useState<Tab>("herkunft")
  const [neuerMitarbeiter, setNeuerMitarbeiter] = useState("")
  const [neueOrganisation, setNeueOrganisation] = useState("")

  const { data: ticket, isLoading } = useQuery({
    queryKey: ["tickets", ticketId],
    queryFn: () => fetchTicket(ticketId),
  })

  const { data: zeitbuchungen } = useQuery({
    queryKey: ["zeitbuchungen", { ticketId }],
    queryFn: () => fetchZeitbuchungen({ ticketId }),
    enabled: tab === "zeiten",
  })

  const invalidieren = () => queryClient.invalidateQueries({ queryKey: ["tickets", ticketId] })

  const speichern = useMutation({
    mutationFn: (patch: Parameters<typeof updateTicket>[1]) => updateTicket(ticketId, patch),
    onSuccess: invalidieren,
    onError: (err) => toast.error(fehlerNachricht(err, "Ticket konnte nicht gespeichert werden.")),
  })

  const recalc = useMutation({
    mutationFn: () => recalcTicketStartDate(ticketId),
    onSuccess: () => {
      invalidieren()
      toast.success("Zeiten wurden aktualisiert.")
    },
    onError: (err) =>
      toast.error(fehlerNachricht(err, "Rückwärtsterminierung konnte nicht durchgeführt werden.")),
  })

  const mitarbeiterZuordnen = useMutation({
    mutationFn: () => zuordneTicketMitarbeiter(ticketId, neuerMitarbeiter),
    onSuccess: () => {
      setNeuerMitarbeiter("")
      invalidieren()
      toast.success("Mitarbeiter wurde zugeordnet.")
    },
    onError: (err) => toast.error(fehlerNachricht(err, "Mitarbeiter konnte nicht zugeordnet werden.")),
  })

  const mitarbeiterEntfernen = useMutation({
    mutationFn: (employeeId: string) => entferneTicketMitarbeiter(ticketId, employeeId),
    onSuccess: invalidieren,
    onError: (err) => toast.error(fehlerNachricht(err, "Mitarbeiter konnte nicht entfernt werden.")),
  })

  const lieferantZuordnen = useMutation({
    mutationFn: () => zuordneTicketLieferant(ticketId, neueOrganisation),
    onSuccess: () => {
      setNeueOrganisation("")
      invalidieren()
      toast.success("Fremdfirma wurde zugeordnet.")
    },
    onError: (err) => toast.error(fehlerNachricht(err, "Fremdfirma konnte nicht zugeordnet werden.")),
  })

  const lieferantenLoeschen = useMutation({
    mutationFn: () => loescheTicketLieferanten(ticketId),
    onSuccess: () => {
      invalidieren()
      toast.success("Lieferanten wurden gelöscht.")
    },
    onError: (err) => toast.error(fehlerNachricht(err, "Lieferanten konnten nicht gelöscht werden.")),
  })

  const stoeranalyseAendern = useMutation({
    mutationFn: (patch: Parameters<typeof updateTicketStoeranalyse>[1]) =>
      updateTicketStoeranalyse(ticketId, patch),
    onSuccess: invalidieren,
    onError: (err) => toast.error(fehlerNachricht(err, "Störanalyse konnte nicht gespeichert werden.")),
  })

  if (isLoading || !ticket) {
    return (
      <div className="animate-pulse space-y-3" aria-label="Lade Ticket …">
        <div className="h-4 w-32 rounded-md bg-slate-100" />
        <div className="h-8 w-80 rounded-md bg-slate-100" />
        <div className="h-40 rounded-md bg-slate-100" />
      </div>
    )
  }

  return (
    <div>
      <header className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">
            {ticket.ticketKey ?? `Ticket #${ticket.id}`}
          </h1>
          <p className="mt-1 text-sm text-slate-600">{ticket.kurzbeschreibung}</p>
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge status={ticket.status} />
          {ticket.alleArbeitenAbgeschlossen && (
            <Badge tone="green">✅ alle Arbeiten abgeschlossen</Badge>
          )}
          <button
            type="button"
            title="Link zur Aufgabe kopieren (A.34)"
            onClick={() => navigator.clipboard?.writeText(ticketDeepLink(ticket.id))}
            className="rounded-md border border-slate-300 px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
          >
            🔗 Link kopieren
          </button>
          <a
            href={HANDBUCH_URL_TICKETS}
            target="_blank"
            rel="noreferrer"
            title="Handbuch anzeigen (H.1)"
            className="rounded-md border border-slate-300 px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
          >
            📖 Handbuch
          </a>
        </div>
      </header>

      <nav className="mb-4 flex gap-1 border-b border-slate-200">
        {TABS.map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={`rounded-t-md px-3 py-2 text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1 ${
              tab === t
                ? "border-b-2 border-slate-900 text-slate-900"
                : "text-slate-500 hover:text-slate-800"
            }`}
          >
            {TAB_LABEL[t]}
          </button>
        ))}
      </nav>

      {tab === "herkunft" && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="rounded-lg border border-slate-200 bg-white p-4">
            <h2 className="mb-3 text-sm font-semibold text-slate-900">Herkunft &amp; Art</h2>
            <dl className="space-y-2 text-sm">
              <Row label="Art des Tickets" wert={TICKET_ART_LABEL[ticket.art]} />
              <Row label="Quelle" wert={ticket.sourceType} />
              <Row label="Quell-ID" wert={ticket.sourceId?.toString() ?? "—"} />
              <Row label="Priorität" wert={ticket.prioritaet ?? "—"} />
            </dl>
            <div className="mt-4 flex gap-2">
              <Select
                value={ticket.status}
                onChange={(e) => speichern.mutate({ status: e.target.value })}
                className="w-auto"
              >
                <option value="neu">Neu</option>
                <option value="offen">Offen</option>
                <option value="in_arbeit">In Arbeit</option>
                <option value="warten">Wartet</option>
                <option value="erledigt">Erledigt</option>
                <option value="zurueckgewiesen">Zurückgewiesen</option>
              </Select>
            </div>
          </div>

          <div className="rounded-lg border border-slate-200 bg-white p-4">
            <h2 className="mb-3 text-sm font-semibold text-slate-900">Terminplanung</h2>
            <dl className="space-y-2 text-sm">
              <Row label="Zeitbedarf (Std.)" wert={ticket.zeitbedarfStunden?.toString() ?? "—"} />
              <Row label="Geplantes Ende" wert={ticket.geplantesEnde ?? "—"} />
              <Row
                label="Spätester Anfangstermin"
                wert={ticket.spaetesterAnfangstermin ?? "—"}
              />
              <Row
                label="Tatsächliche Fertigstellung"
                wert={ticket.tatsaechlicheFertigstellung ?? "—"}
              />
            </dl>
            <Button variant="secondary" onClick={() => recalc.mutate()} className="mt-4">
              Zeiten aktualisieren (Rückwärtsterminierung)
            </Button>
          </div>

          <div className="rounded-lg border border-slate-200 bg-white p-4 sm:col-span-2">
            <h2 className="mb-3 text-sm font-semibold text-slate-900">Störanalyse (09-F6)</h2>
            <div className="flex items-center gap-2">
              <Select
                value={ticket.stoeranalyseNotwendig ?? ""}
                onChange={(e) =>
                  stoeranalyseAendern.mutate({
                    stoeranalyseNotwendig: e.target.value || null,
                    stoeranalyse: ticket.stoeranalyse,
                  })
                }
                className="w-auto"
              >
                <option value="">— nicht festgelegt —</option>
                <option value="ja">Ja</option>
                <option value="nein">Nein</option>
                <option value="unklar">Unklar</option>
              </Select>
              <span className="text-xs text-slate-500">
                „Nein" löscht den Störanalyse-Text unwiderruflich (A.15).
              </span>
            </div>
            {ticket.stoeranalyseNotwendig !== "nein" && (
              <Textarea
                defaultValue={ticket.stoeranalyse ?? ""}
                onBlur={(e) =>
                  stoeranalyseAendern.mutate({
                    stoeranalyseNotwendig: ticket.stoeranalyseNotwendig,
                    stoeranalyse: e.target.value || null,
                  })
                }
                className="mt-3"
                rows={4}
                placeholder="Störanalyse-Freitext…"
              />
            )}
          </div>
        </div>
      )}

      {tab === "berichte" && (
        <BerichtsThread objektTyp="ticket" objektId={String(ticket.id)} />
      )}

      {tab === "mitarbeiter" && (
        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <form
            className="mb-4 flex items-end gap-2"
            onSubmit={(e) => {
              e.preventDefault()
              if (neuerMitarbeiter.trim()) mitarbeiterZuordnen.mutate()
            }}
          >
            <div className="w-64">
              <EmployeePicker
                value={neuerMitarbeiter || null}
                onChange={(id) => setNeuerMitarbeiter(id ?? "")}
                label="Mitarbeiter zuordnen"
              />
            </div>
            <Button type="submit">Zuordnen</Button>
          </form>
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b border-slate-300 text-left text-slate-500">
                <th className="py-2 pr-4">Mitarbeiter</th>
                <th className="py-2 pr-4">Verantwortlich</th>
                <th className="py-2 pr-4">Abgeschlossen</th>
                <th className="py-2" />
              </tr>
            </thead>
            <tbody>
              {ticket.mitarbeiter.map((m) => (
                <TicketMitarbeiterRow
                  key={m.id}
                  employeeId={m.employeeId}
                  verantwortlich={m.verantwortlich}
                  abgeschlossen={m.abgeschlossen}
                  onEntfernen={() => mitarbeiterEntfernen.mutate(m.employeeId)}
                />
              ))}
              {ticket.mitarbeiter.length === 0 && (
                <tr>
                  <td colSpan={4} className="py-6 text-center text-slate-400">
                    Keine Mitarbeiter zugeordnet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          <p className="mt-3 text-xs text-slate-500">
            Abschluss erfolgt über die TimeTab-Zeiterfassung
            („Serviceticket abschließen"); sobald alle zugeordneten
            Mitarbeiter abgeschlossen haben, wird das Ticket automatisch auf
            „Erledigt" gesetzt (A.2b/A.33/A.39).
          </p>
        </div>
      )}

      {tab === "zeiten" && (
        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b border-slate-300 text-left text-slate-500">
                <th className="py-2 pr-4">Datum</th>
                <th className="py-2 pr-4">Mitarbeiter</th>
                <th className="py-2 pr-4">Stunden</th>
                <th className="py-2 pr-4">Navision-Nr.</th>
                <th className="py-2">Leistung</th>
              </tr>
            </thead>
            <tbody>
              {(zeitbuchungen ?? []).map((z) => (
                <tr key={z.id} className="border-b border-slate-100">
                  <td className="py-2 pr-4">{z.datum}</td>
                  <td className="py-2 pr-4">{z.employeeId}</td>
                  <td className="py-2 pr-4">{z.stunden}</td>
                  <td className="py-2 pr-4">{z.navisionNr ?? "—"}</td>
                  <td className="py-2">{z.leistungAnzeige ?? "—"}</td>
                </tr>
              ))}
              {(zeitbuchungen ?? []).length === 0 && (
                <tr>
                  <td colSpan={5} className="py-6 text-center text-slate-400">
                    Keine Zeitbuchungen für dieses Ticket.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {tab === "fremdfirmen" && (
        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <form
            className="mb-4 flex gap-2"
            onSubmit={(e) => {
              e.preventDefault()
              if (neueOrganisation.trim()) lieferantZuordnen.mutate()
            }}
          >
            <Input
              type="text"
              value={neueOrganisation}
              onChange={(e) => setNeueOrganisation(e.target.value)}
              placeholder="organisation_id (Stammdaten-API)"
              className="w-auto"
            />
            <Button type="submit">Fremdfirma zuordnen</Button>
          </form>
          <ul className="space-y-1 text-sm">
            {ticket.lieferanten.map((l) => (
              <li key={l.id} className="rounded-md border border-slate-200 px-3 py-1.5">
                {l.organisationId}
              </li>
            ))}
            {ticket.lieferanten.length === 0 && (
              <li className="text-slate-400">Keine Fremdfirmen zugeordnet.</li>
            )}
          </ul>
          {ticket.lieferanten.length > 0 && !ticket.unterstuetzungExtern && (
            <Button
              variant="destructive"
              onClick={() => lieferantenLoeschen.mutate()}
              className="mt-3 h-auto px-3 py-1.5 text-xs"
            >
              Lieferanten löschen (A.36 — Unterstützung durch extern wurde deaktiviert)
            </Button>
          )}
        </div>
      )}
    </div>
  )
}

function Row({ label, wert }: { label: string; wert: string }) {
  return (
    <div className="flex justify-between border-b border-slate-100 pb-1">
      <dt className="text-slate-500">{label}</dt>
      <dd className="font-medium text-slate-900">{wert}</dd>
    </div>
  )
}

/** Zeile mit aufgelöstem Mitarbeiternamen (Muster UX_KONZEPT.md §2.4: „Name
 * (Kürzel)" über den gecachten employees-Endpoint; unauflösbare IDs als
 * <code>-ID mit Tooltip). */
function TicketMitarbeiterRow({
  employeeId,
  verantwortlich,
  abgeschlossen,
  onEntfernen,
}: {
  employeeId: string
  verantwortlich: boolean
  abgeschlossen: boolean
  onEntfernen: () => void
}) {
  const label = useEmployeeLabel(employeeId)
  return (
    <tr className="border-b border-slate-100">
      <td className="py-2 pr-4">
        {label ?? (
          <code title="Mitarbeiter nicht (mehr) im Cache" className="text-xs text-slate-500">
            {employeeId}
          </code>
        )}
      </td>
      <td className="py-2 pr-4">{verantwortlich ? "Ja" : "—"}</td>
      <td className="py-2 pr-4">{abgeschlossen ? "✅" : "❌"}</td>
      <td className="py-2 text-right">
        <button
          type="button"
          onClick={onEntfernen}
          className="rounded-md text-xs text-red-600 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
        >
          entfernen
        </button>
      </td>
    </tr>
  )
}
